import { CenterContainer } from 'components/common/CenterContainer'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'
import Footer from 'components/Footer'
import { NextSeo } from 'next-seo'
import EffectiveNav from 'components/Nav/EffectiveNav'
import Typography from 'components/common/Typography'
import HelpFAQ from 'components/discover/DiscoverEvents/HelpFAQ'
import { useWindowDimensions } from 'hooks'
import tailwind from 'lib/tailwind'
import ModalTrigger from 'components/common/ModalTrigger'
import VideoEmbedModal from 'components/VideoEmbedModal/VideoEmbedModal'
import moment from 'moment'
import { motion } from 'framer-motion'
import { NoSSRCarousel } from 'components/common/NoSSRPageScroll'
import TrackableClick from 'components/common/TrackableClick'
import MFDAnnouncementBanner from 'components/Collectives/Common/MFDAnnouncementBanner'

const heroWidthHeight = 450

const metaImage =
  'https://i.upstreamapp.com/ogImages/collectives/investment-dao-open-graph.png'
const metaImageAlt = `The DAO for investment Clubs`
const metaUrl = 'https://upstreamapp.com/investment-dao'

const FeatureGridContent = [
  {
    image: '/static/investmentDao/lock.svg',
    imageSize: { width: 64, height: 88 },
    title: 'DAO Vault',
    description:
      'Upstream uses Gnosis multisig wallets for industry-leading security.'
  },
  {
    image: '/static/investmentDao/governance.svg',
    imageSize: { width: 77, height: 88 },
    title: 'Governance',
    description: `Members receive governance tokens proportional to their contribution to vote on proposals.
      `
  },
  {
    image: '/static/investmentDao/voting.svg',
    imageSize: { width: 89, height: 88 },
    title: 'Voting',
    description: `Vote on proposals to make decisions. Once a proposal passes, it's executed on-chain by designated signators.`
  },
  {
    image: '/static/investmentDao/polls.svg',
    imageSize: { width: 68, height: 86 },
    title: 'Polls',
    description: `Take a quick pulse of community sentiment with polls.`
  },
  {
    image: '/static/investmentDao/chat.svg',
    imageSize: { width: 82, height: 82 },
    title: 'Chat',
    description: `Stay connected to fellow DAO members and participate in discussions.`
  },
  {
    image: '/static/investmentDao/events.svg',
    imageSize: { width: 82, height: 80 },
    title: 'Events',
    description: `Host your DAO Town Halls and weekly meetings on Upstream’s in-platform event function.`
  },
  {
    image: '/static/investmentDao/legal.svg',
    imageSize: { width: 87, height: 86 },
    title: 'Legal Support',
    description: `Get your questions answered by independent legal counsel.`
  }
]

const PeopleSayingContent = [
  {
    text: (
      <>
        Wow, what a wild ride today with{' '}
        <Typography color="blue" component="span" size="inherit">
          @OthersideMeta
        </Typography>
        {', '}
        <Typography color="blue" component="span" size="inherit">
          @ajt
        </Typography>{' '}
        and the{' '}
        <Typography color="blue" component="span" size="inherit">
          @joinupstream
        </Typography>{' '}
        platform were absolutely essential to the quick formation and amazing
        execution of our Metaverse Land Baron DAO. Super glad to have weathered
        the storm with these fine folks 🤝
      </>
    ),
    image: '/static/investmentDao/barons-dao-tweet.png',
    imageSize: { width: 120, height: 120 },
    title: `Baron's DAO`,
    userRef: '@entropyeq_',
    link: `https://twitter.com/entropyeq_/status/1520621089130381312?s=20&t=8ZsTR_A8I6un5qUoCrfX0g`
  },
  {
    text: (
      <>
        We’re officially live! Thanks{' '}
        <Typography color="blue" component="span" size="inherit">
          @joinupstream
        </Typography>{' '}
        for facilitating the whole process! And to celebrate it, enjoy this
        beautiful piece of art of the great{' '}
        <Typography color="blue" component="span" size="inherit">
          @Rezaniaofficial
        </Typography>{' '}
        , “Amnesia”!
      </>
    ),
    image: '/static/investmentDao/3xg-dao-tweeet.png',
    imageSize: { width: 120, height: 120 },
    title: '3XG DAO',
    userRef: '@3XGdao',
    link: `https://twitter.com/3XGdao/status/1555860917031690244?s=20&t=Jb7TTs2Cp1JDVPAA1OHhSg`
  },
  {
    text: (
      <>
        We are proud to announce that{' '}
        <Typography color="blue" component="span" size="inherit">
          @DaoManor
        </Typography>{' '}
        is functional on{' '}
        <Typography color="blue" component="span" size="inherit">
          @joinupstream
        </Typography>
        {'. '} We will be using Upstream {`DAO's`}
        all-in-one DAO stack to run our DAO operations. #DAO #manordao #defi
        #Decentralization #Web3 #cryptocurrecy #Crypto #upstream #daostack
        #blockchain #Ethereum
      </>
    ),
    image: '/static/investmentDao/manor-dao-tweet.png',
    imageSize: { width: 120, height: 120 },
    title: 'ManorDAO',
    userRef: '@DAOManor',
    link: `https://twitter.com/DaoManor/status/1553156199931166720?s=20&t=8ZsTR_A8I6un5qUoCrfX0g`
  }
]

const SUCCESS_STORIES = [
  {
    text: (
      <>
        A group of diverse operators, investors, and founders came together to
        form a women-led investment club on the blockchain to generate wealth
        for their members.
      </>
    ),
    image: '/static/investmentDao/joandao.png',
    imageSize: { width: 170, height: 170 },
    title: 'JoanDAO',
    org: 'Women-Led Investment DAO'
  },
  {
    text: (
      <>
        This group of 30+ web3 natives came together with the purpose of buying
        land together in the metaverse. Since launch they have purchased 20
        premium land plots.
      </>
    ),
    image: '/static/investmentDao/barons-dao.png',
    imageSize: { width: 170, height: 170 },
    title: 'Baron’s DAO',
    org: 'Buying Land in the Metaverse'
  }
]

function CollectivesPage() {
  const whiteBoxStyle = {
    borderRadius: '10px',
    boxShadow: '0px 4px 45px rgb(0,0,0, 0.04)'
  }

  const screen = useWindowDimensions()
  const xlBreakpoint = Number(tailwind.theme?.screens?.['xl'].replace('px', ''))
  const smBreakpoint = Number(tailwind.theme?.screens?.['sm'].replace('px', ''))

  const isLarge = (screen.width || 1000) < xlBreakpoint
  const isXSmall = (screen.width || 1000) < smBreakpoint

  const [peoplsSayingItemWidthPx, peopleSayingItemWidthPx] = useState(590)

  useEffect(() => {
    if (!screen.width) {
      return
    }
    peopleSayingItemWidthPx(
      isXSmall ? screen.width * 0.8 : isLarge ? 500 : 590 // screen.width * 0.333 - 40
    )
  }, [isLarge, isXSmall, screen.width])

  return (
    <>
      <NextSeo
        openGraph={{
          url: metaUrl,
          images: [
            {
              url: metaImage,
              width: 1200,
              height: 630,
              alt: metaImageAlt
            }
          ]
        }}
        twitter={{
          cardType: 'summary_large_image'
        }}
        additionalMetaTags={[
          {
            name: 'twitter:image',
            content: metaImage
          },
          {
            name: 'twitter:image:alt',
            content: 'DAOs Introduction Flyer'
          }
        ]}
      />

      <MFDAnnouncementBanner bannerStyle="grey" />

      <EffectiveNav />

      <div className="w-full -mt-24">
        <div className="landing-page-gradient-box">
          <div className="px-8 lg:px-0 pt-10 flex flex-1 flex-col items-center md:items-start md:justify-between md:flex-row-reverse max-w-6xl w-full">
            <div className="mt-8">
              <Image
                src="/static/investmentDao/hero.svg"
                width={heroWidthHeight}
                height={heroWidthHeight}
                alt="Upstream DAO Hero"
              />
            </div>

            <div className="sm:w-[30rem] mt-12 sm:mt-10 text-center sm:text-left">
              <Typography
                className="flex sm:hidden text-5xl"
                fontWeight="black"
              >
                Start Your Investment Club DAO
              </Typography>

              <Typography
                className="hidden sm:flex text-7xl"
                fontWeight="black"
              >
                Start Your Investment Club DAO
              </Typography>

              <div className="mt-4">
                <h1 className="font-nunito text-[22px]">
                  Invest together using a DAO to coordinate funds and make
                  decisions
                </h1>
                <br />
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center mt-20 px-8 w-full">
            <Typography className="text-3xl" fontWeight="bold">
              Key DAO Features
            </Typography>

            <div className="hidden sm:grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-8 mt-8 w-full max-w-6xl">
              {FeatureGridContent.map(content => (
                <div
                  className="lg:aspect-w-10 lg:aspect-h-8 w-full"
                  key={content.title}
                >
                  <div className="bg-white border-[1px] border-solid border-[#e5e7eb] rounded-[10px] p-7 flex flex-col shadow-[0px_4px_45px_rgb(0,0,0,0.04)] hover:shadow-[0px_4px_45px rgb(0,0,0,0.1)] transition-all duration-200 ease-out">
                    <div className="flex-1">
                      <Image
                        alt={content.title}
                        src={content.image}
                        width={content.imageSize.width}
                        height={content.imageSize.height}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col space-y-2">
                        <Typography fontWeight="bold" size="heading">
                          {content.title}
                        </Typography>
                        <Typography
                          fontWeight="light"
                          className="text-sm md:text-md xl:text-lg"
                        >
                          {content.description}
                        </Typography>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="sm:hidden mt-8 w-full">
              <NoSSRCarousel
                itemWidth={320}
                items={FeatureGridContent.map(content => (
                  <div
                    style={whiteBoxStyle}
                    className="w-full bg-white border shadow-lg rounded-lg p-4 h-full"
                    key={content.title}
                  >
                    <div className="p-4 flex flex-col">
                      <div className="flex-1">
                        <Image
                          alt={content.title}
                          src={content.image}
                          width={content.imageSize.width}
                          height={content.imageSize.height}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-col space-y-2">
                          <Typography size="heading">
                            {content.title}
                          </Typography>
                          <Typography className="text-sm md:text-md lg:text-lg">
                            {content.description}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              />
            </div>
          </div>
        </div>
      </div>

      <CenterContainer className="mt-8 mb-20">
        <div className="flex flex-row justify-center py-12 w-full px-4">
          <Typography className="text-3xl" fontWeight="bold">
            Investment Club Success Stories
          </Typography>
        </div>

        <div className="flex mx-8 space-y-[12rem] md:space-y-0 md:space-x-8 flex-col md:flex-row mt-20 max-w-6xl">
          {SUCCESS_STORIES.map((item, i) => (
            <div
              style={whiteBoxStyle}
              className="flex-1 flex flex-col border items-center rounded shadow bg-white relative p-10 hover:scale-105 ease-linear duration-200"
              key={i}
            >
              <div className="absolute -top-20">
                <Image
                  alt={item.title}
                  src={item.image}
                  width={item.imageSize.width}
                  height={item.imageSize.height}
                />
              </div>

              <div className="flex flex-col items-center mt-24">
                <Typography size="heading">{item.title}</Typography>
                <Typography size="lg">{item.org}</Typography>
              </div>

              <div className="mt-6 hidden sm:block">
                <Typography fontWeight="light" size="lg" color="gray">
                  {item.text}
                </Typography>
              </div>

              <div className="mt-6 sm:hidden">
                <Typography size="md" color="gray">
                  {item.text}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      </CenterContainer>

      <div className="bg-gradient-to-r from-[#68A2FF] to-[#F0A6FD] p-10 overflow-x-hidden mt-8">
        <div className="flex flex-row items-center space-x-8 pt-8 pb-10">
          <Typography className="text-3xl" fontWeight="bold" color="white">
            What People are Saying
          </Typography>
        </div>

        <NoSSRCarousel
          itemWidth={peoplsSayingItemWidthPx}
          items={PeopleSayingContent.map((item, i) => (
            <a
              style={whiteBoxStyle}
              className="bg-white rounded-lg px-10 py-10 shrink-0 flex flex-col h-full justify-between hover:shadow-xl"
              href={item.link}
              key={i}
              target="_blank"
              rel="noreferrer"
            >
              <div className="pb-8 hidden sm:block">
                <Typography size="lg" component={'div'}>
                  {item.text}
                </Typography>
              </div>

              <div className="pb-8 sm:hidden">
                <Typography size="md" component={'div'}>
                  {item.text}
                </Typography>
              </div>

              <div className=" flex flex-row items-center space-x-4">
                <Image
                  alt={item.title}
                  src={item.image}
                  width={item.imageSize.width}
                  height={item.imageSize.height}
                />

                <div className="flex flex-col">
                  <Typography size="heading">{item.title}</Typography>
                  <Typography color="gray">{item.userRef}</Typography>
                </div>
              </div>
            </a>
          ))}
        />
        {/* )} */}
      </div>

      <CenterContainer className="bg-gradient-to-r from-[#E7EDFD] via-[#E7EDFD] to-[#F8F1FE] px-10">
        <div className="overflow-hidden pt-12 mt-4 max-w-6xl">
          <div className="flex flex-col lg:flex-row lg:items-center">
            <div className="flex flex-col flex-[1_0_0%] space-y-4 pr-8 pl-4">
              <Typography className="text-[4rem]" fontWeight="black">
                Get Started
              </Typography>
              <Typography size="xl" fontWeight="light">
                See how a DAO makes it easy to make group investments more
                effectively than traditional investment vehicles.{' '}
              </Typography>
            </div>

            <div className="flex-[2_0_0%] mt-8 lg:mt-0">
              <motion.div
                whileHover={{
                  scale: 1.02,
                  transition: { duration: 0.2 }
                }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="lg:min-w-[600px] xl:min-w-[700px] relative group cursor-pointer">
                  <Image
                    alt="Demo screen shot"
                    src="/static/events/demo_screen_shot.png"
                    width={802}
                    height={445}
                  />

                  <ModalTrigger
                    trigger={onClick => (
                      <TrackableClick event="INVESTMENT-DAO Page: video opened">
                        <div
                          className="bg-no-repeat bg-center bg-[length:92px_92px] inset-0 opacity-80 group-hover:opacity-100 absolute cursor-pointer"
                          style={{
                            backgroundImage:
                              'url(/static/investmentDao/play-button.png)'
                          }}
                          onClick={onClick}
                        />
                      </TrackableClick>
                    )}
                  >
                    <VideoEmbedModal
                      title={`Upstream Tutorial`}
                      postedTimestamp={moment('2022-04-06').toDate()}
                      url={`https://www.youtube.com/embed/Q_XyIrZvSq8`}
                      width={800}
                    />
                  </ModalTrigger>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </CenterContainer>

      <CenterContainer>
        <div className="px-10 mt-16 w-full md:w-4/5 xl:w-3/5">
          <Typography size="default" fontWeight="light">
            Disclaimer: The information provided by Upstream does not constitute
            legal advice, nor is it intended to take the place of the advice of
            independent legal counsel. Please consult the{' '}
            <Typography
              size="inherit"
              color="blue"
              newTab
              href="https://www.sec.gov/reportspubs/investor-publications/investorpubsinvclubhtm.html#:~:text=The%20SEC%20generally%20does%20not,of%201933%20(Securities%20Act)."
            >
              SEC guidelines on investment clubs.
            </Typography>
          </Typography>
        </div>
      </CenterContainer>

      <HelpFAQ />
      <Footer />
    </>
  )
}

export default CollectivesPage
